import { motion } from 'framer-motion';
import { Search, FileText, Zap, BarChart3, Play } from 'lucide-react';
import ChapterIndicator from './ChapterIndicator';
import { useI18n } from '@/contexts/I18nContext';

const CinematicProcess = () => {
  const { t } = useI18n();

  const steps = [
    {
      number: '01', icon: Search, title: t.process.step1Title, description: t.process.step1Desc,
      duration: t.process.step1Duration, color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20'
    },
    {
      number: '02', icon: FileText, title: t.process.step2Title, description: t.process.step2Desc,
      duration: t.process.step2Duration, color: 'from-emerald-500 to-emerald-600', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20'
    },
    {
      number: '03', icon: Zap, title: t.process.step3Title, description: t.process.step3Desc,
      duration: t.process.step3Duration, color: 'from-amber-500 to-amber-600', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20'
    },
    {
      number: '04', icon: BarChart3, title: t.process.step4Title, description: t.process.step4Desc,
      duration: t.process.step4Duration, color: 'from-punto to-red-600', bgColor: 'bg-punto/10', borderColor: 'border-punto/20'
    }
  ];
  return (
    <section className="relative py-8 md:py-12 bg-gradient-to-b from-gray-100 via-gray-50 to-gray-100 dark:from-cinema-dark dark:via-[#0d0d0f] dark:to-cinema-dark overflow-hidden">
      {/* Film Strip Effect - Top - Hidden on mobile */}
      <div className="hidden sm:flex absolute top-0 left-0 right-0 h-6 md:h-8 bg-gray-200 dark:bg-[#0a0a0a] border-b border-gray-300 dark:border-white/5 items-center justify-between px-4 md:px-6 z-10">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="w-1.5 md:w-2 h-3 md:h-4 rounded-sm bg-gray-300 dark:bg-cinema-surface/50" />
        ))}
      </div>
      
      {/* Film Strip Effect - Bottom - Hidden on mobile */}
      <div className="hidden sm:flex absolute bottom-0 left-0 right-0 h-6 md:h-8 bg-gray-200 dark:bg-[#0a0a0a] border-t border-gray-300 dark:border-white/5 items-center justify-between px-4 md:px-6 z-10">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="w-1.5 md:w-2 h-3 md:h-4 rounded-sm bg-gray-300 dark:bg-cinema-surface/50" />
        ))}
      </div>

      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Chapter Indicator */}
        <div className="flex justify-center">
          <ChapterIndicator act={t.process.act} subtitle={t.process.actSubtitle} light />
        </div>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-6"
        >
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-gray-900 dark:text-white mb-1">
            {t.process.title}
            <span className="text-purple-500 dark:text-purple-400"> {t.process.titleAccent}</span>
          </h2>
          <p className="text-gray-500 dark:text-white/50 text-xs md:text-sm max-w-md mx-auto px-4">
            {t.process.subtitle}
          </p>
        </motion.div>

        {/* Process Steps - 2 cols mobile, 4 cols desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="relative"
            >
              {/* Connector Line - Desktop */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 -right-1.5 w-3">
                  <motion.div 
                    className="h-[2px] bg-gradient-to-r from-gray-300 dark:from-white/20 to-gray-200 dark:to-white/5"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                  />
                </div>
              )}
              
              <div className={`${step.bgColor} backdrop-blur-sm border ${step.borderColor} rounded-xl p-3 md:p-4 h-full hover:border-gray-400 dark:hover:border-white/20 transition-all duration-300 group`}>
                {/* Step Number */}
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                    <step.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className={`text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.color} opacity-50`}>
                    {step.number}
                  </span>
                </div>
                
                {/* Content */}
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  {step.title}
                </h3>
                <p className="text-gray-500 dark:text-white/50 text-[11px] leading-relaxed mb-2 group-hover:text-gray-600 dark:group-hover:text-white/70 transition-colors line-clamp-2">
                  {step.description}
                </p>
                
                {/* Duration Badge */}
                <div className={`inline-flex items-center px-2 py-1 rounded-md ${step.bgColor} border ${step.borderColor}`}>
                  <span className="text-gray-600 dark:text-white/70 text-[10px] font-medium">⏱ {step.duration}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CinematicProcess;
